import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import GoogleMapContext from '../../context/GoogleMapContext';
import GoogleMapAdvancedMarker from '../GoogleMapAdvancedMarker/GoogleMapAdvancedMarker';
import GoogleMapGroundOverlay from '../GoogleMapGroundOverlay/GoogleMapGroundOverlay';
import GoogleMapPolygon from '../GoogleMapPolygon/GoogleMapPolygon';
import SelectDropdown from '@components/SelectDropDown/SelectDropDown';
import { ChevronDownIcon } from '@icon/index';

/* -------------------------------------------------------------------------------------------------
 * Component: GoogleMap
 * -----------------------------------------------------------------------------------------------*/

interface GoogleMapComposition {
    AdvancedMarker: typeof GoogleMapAdvancedMarker;
    GroundOverlay: typeof GoogleMapGroundOverlay;
    Polygon: typeof GoogleMapPolygon;
}

interface GoogleMapProps {
    center: google.maps.LatLng | google.maps.LatLngLiteral;
    zoom: number;
    children?: ReactNode;
    onLoad?: (map: google.maps.Map) => void;
}

const GoogleMap: FC<GoogleMapProps> & GoogleMapComposition = (props) => {
    const { children, center, zoom, onLoad } = props;

    const mapRef = useRef(null);
    const [map, setMap] = useState<google.maps.Map>(null);
    const [mapTypeId, setMapTypeId] = useState<google.maps.MapTypeId>(google.maps.MapTypeId.ROADMAP);

    useEffect(() => {
        if (typeof google !== 'object') throw new Error('Please load google maps script before using <GoogleMap/>');

        const map = new google.maps.Map(mapRef.current, {
            zoom: zoom,
            center: center,
            mapId: '4e988217a576730a',
            disableDefaultUI: true,
            minZoom: 3,
            scrollwheel: true,
            zoomControl: false,
            mapTypeId: mapTypeId,
        });

        setMap(map);
        onLoad?.(map);
    }, [center, mapTypeId, onLoad, zoom]);

    useEffect(() => {
        if (map) {
            map.setMapTypeId(mapTypeId);
        }
    }, [map, mapTypeId]);

    const toggleMapType = () => {
        setMapTypeId((prevType) =>
            prevType === google.maps.MapTypeId.ROADMAP ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP
        );
    };

    const filterOptions = useMemo(() => {
        const _filterOptions = [
            {
                label: (
                    <div className="flex justify-between items-center gap-2">
                        <svg
                            className="w-5 h-5 text-gray-700"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                            />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">Map</span>
                    </div>
                ),
                value: google.maps.MapTypeId.ROADMAP,
            },
            {
                label: (
                    <div className="flex justify-between items-center gap-2">
                        <svg
                            className="w-5 h-5 text-gray-700"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">Satellite</span>
                    </div>
                ),
                value: google.maps.MapTypeId.SATELLITE,
            },
        ];

        return _filterOptions as [];
    }, []);

    return (
        <GoogleMapContext.Provider value={{ map }}>
            <div className="w-full h-full relative outline-none">
                <div ref={mapRef} className="w-full h-full">
                    {children}
                </div>

                <SelectDropdown
                    data-cy="map-view-change-dropdown"
                    className="btn-dropdown btn-secondary btn-secondary-hover absolute bottom-6 left-6"
                    value={mapTypeId}
                    onChange={toggleMapType}
                    options={filterOptions}
                    render={(label) => (
                        <span className="flex items-center gap-2 text-grayscale-500 font-medium">
                            <span className="text-grayscale-900 font-semibold">{label}</span>{' '}
                            <span>
                                <ChevronDownIcon className="text-grayscale-400" strokeWidth="2" />
                            </span>
                        </span>
                    )}
                />
            </div>
        </GoogleMapContext.Provider>
    );
};

GoogleMap.AdvancedMarker = GoogleMapAdvancedMarker;
GoogleMap.GroundOverlay = GoogleMapGroundOverlay;
GoogleMap.Polygon = GoogleMapPolygon;

export default GoogleMap;
